/**
 * Nettoyage d'un événement d'erreur avant son départ vers le suivi des
 * erreurs (GlitchTip auto-hébergé, ADR 0013).
 *
 * Le principe est une **liste blanche** : l'événement envoyé est reconstruit
 * à partir des seuls champs nommés ici. Un champ que le SDK ajouterait demain
 * ne part pas tant que quelqu'un ne l'a pas ajouté à cette liste, en revue.
 * Une liste noire (« retirer les cookies, retirer le corps… ») laisserait
 * passer tout ce qu'on n'a pas prévu.
 *
 * Ce qui ne part jamais :
 * - le corps de la requête, ses paramètres d'URL, ses en-têtes (seul
 *   `User-Agent` est gardé) — donc ni jeton, ni cookie ;
 * - le **message** des exceptions : il peut avoir été construit à partir
 *   d'une donnée patient (« Patient RAKOTO introuvable »), et une erreur
 *   Prisma recopie les valeurs de la requête. Restent le type et la pile ;
 *   le détail se retrouve dans les journaux du serveur, par la référence ;
 * - l'utilisateur au-delà de son identifiant et de son rôle ;
 * - les sorties console et les clics dans le fil d'Ariane ;
 * - `extra`, les contextes non techniques, les variables locales des piles.
 *
 * Partagé entre l'API (son propre SDK, et le relais des événements du
 * navigateur) et le front : une seule règle, deux points d'application.
 * Volontairement sans dépendance au SDK : `@afs/shared` n'importe rien.
 */
export const VALEUR_EXPURGEE = '[expurgé]';
/**
 * Réglage `dataCollection` du SDK, pour l'API comme pour le front : il ne
 * collecte rien de lui-même. Depuis la version 11, le SDK collecte par défaut
 * cookies, en-têtes, paramètres d'URL et corps ; le nettoyage le rattraperait,
 * mais ce qui n'est pas collecté ne peut pas fuir.
 */
export const COLLECTE_MINIMALE_SUIVI = {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: { allow: ['User-Agent'] }, response: false },
    httpBodies: [],
    urlQueryParams: false,
    graphQL: { document: false, variables: false },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    stackFrameVariables: false,
    frameContextLines: 0,
};
/** Champs scalaires de premier niveau recopiés tels quels. */
const CHAMPS_RACINE = [
    'event_id',
    'timestamp',
    'level',
    'platform',
    'logger',
    'release',
    'environment',
    'dist',
    'server_name',
    'sdk',
    'fingerprint',
];
/**
 * Étiquettes posées par la plateforme elle-même — aucune autre ne part.
 * `role` double `user.role` : GlitchTip ne garde de l'utilisateur que son
 * identifiant (constaté le 16/10 sur GlitchTip 6), l'étiquette permet de trier.
 */
export const ETIQUETTES_SUIVI = ['requete', 'route', 'methode', 'role'];
/** Contextes purement techniques : version du navigateur, du système, de Node. */
const CONTEXTES_TECHNIQUES = ['os', 'runtime', 'browser', 'device', 'app', 'trace'];
/** Ce qui reste d'un cadre de pile : où, jamais avec quelles valeurs. */
const CHAMPS_CADRE = ['function', 'module', 'lineno', 'colno', 'in_app', 'platform'];
/** Catégories du fil d'Ariane qui disent où l'on est passé, sans rien de saisi. */
const CATEGORIES_FIL = ['navigation', 'fetch', 'xhr', 'http'];
function estObjet(valeur) {
    return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}
function estScalaire(valeur) {
    return typeof valeur === 'string' || typeof valeur === 'number' || typeof valeur === 'boolean';
}
function recopier(source, champs) {
    const copie = {};
    for (const champ of champs) {
        if (source[champ] !== undefined)
            copie[champ] = source[champ];
    }
    return copie;
}
/**
 * Un segment de chemin qui porte une valeur plutôt qu'un nom d'écran :
 * identifiant, jeton de réinitialisation (`/mot-de-passe-oublie/:jeton`),
 * matricule. Un nom d'écran n'a ni chiffre ni vingt caractères.
 */
function estValeur(segment) {
    return segment.length >= 20 || (/\d/.test(segment) && segment.length >= 6);
}
function retirerParametres(adresse) {
    return adresse.replace(/[?#].*$/s, '');
}
/**
 * Une adresse sans ses paramètres, sans son ancre, et sans les valeurs que
 * porte son chemin. `?recherche=` porte un nom ou un matricule.
 */
export function adresseSansDonnees(adresse) {
    const sansParametres = retirerParametres(adresse);
    const origine = /^[a-z][a-z0-9+.-]*:\/\/[^/]*/i.exec(sansParametres)?.[0] ?? '';
    const chemin = sansParametres.slice(origine.length);
    return (origine +
        chemin
            .split('/')
            .map((segment) => (estValeur(segment) ? ':valeur' : segment))
            .join('/'));
}
function nettoyerCadre(cadre) {
    if (!estObjet(cadre))
        return null;
    const propre = recopier(cadre, CHAMPS_CADRE);
    // Un fichier de code, pas une page : son nom haché (`index-a1b2c3.js`) sert
    // à retrouver la source, on n'en retire que les paramètres.
    for (const champ of ['filename', 'abs_path']) {
        const valeur = cadre[champ];
        if (typeof valeur === 'string')
            propre[champ] = retirerParametres(valeur);
    }
    return propre;
}
function nettoyerException(exception) {
    if (!estObjet(exception))
        return null;
    const propre = { value: VALEUR_EXPURGEE };
    if (typeof exception.type === 'string')
        propre.type = exception.type;
    if (estObjet(exception.mechanism)) {
        propre.mechanism = recopier(exception.mechanism, ['type', 'handled', 'synthetic']);
    }
    const cadres = estObjet(exception.stacktrace) ? exception.stacktrace.frames : undefined;
    if (Array.isArray(cadres)) {
        propre.stacktrace = { frames: cadres.map(nettoyerCadre).filter((c) => c !== null) };
    }
    return propre;
}
function nettoyerRequete(requete) {
    if (!estObjet(requete))
        return undefined;
    const propre = {};
    if (typeof requete.method === 'string')
        propre.method = requete.method;
    if (typeof requete.url === 'string')
        propre.url = adresseSansDonnees(requete.url);
    if (estObjet(requete.headers)) {
        const agent = Object.entries(requete.headers).find(([nom]) => nom.toLowerCase() === 'user-agent')?.[1];
        if (typeof agent === 'string')
            propre.headers = { 'User-Agent': agent };
    }
    return propre;
}
function nettoyerUtilisateur(utilisateur) {
    if (!estObjet(utilisateur))
        return undefined;
    const propre = {};
    if (estScalaire(utilisateur.id))
        propre.id = String(utilisateur.id);
    if (typeof utilisateur.role === 'string')
        propre.role = utilisateur.role;
    return Object.keys(propre).length > 0 ? propre : undefined;
}
function nettoyerEtiquettes(etiquettes) {
    if (!estObjet(etiquettes))
        return undefined;
    const propre = {};
    for (const cle of ETIQUETTES_SUIVI) {
        const valeur = etiquettes[cle];
        if (estScalaire(valeur)) {
            propre[cle] = cle === 'route' ? adresseSansDonnees(String(valeur)) : valeur;
        }
    }
    return propre;
}
function nettoyerContextes(contextes) {
    if (!estObjet(contextes))
        return undefined;
    const propre = {};
    for (const nom of CONTEXTES_TECHNIQUES) {
        const contexte = contextes[nom];
        if (!estObjet(contexte))
            continue;
        propre[nom] = Object.fromEntries(Object.entries(contexte).filter(([, valeur]) => estScalaire(valeur)));
    }
    return propre;
}
/**
 * Un élément du fil d'Ariane, ou `null` s'il ne doit pas partir.
 *
 * Les sorties console sont écartées (elles recopient ce qu'on y a écrit),
 * comme les clics et les saisies : le sélecteur d'un bouton peut porter son
 * titre, et le titre un nom. Restent les navigations et les appels réseau,
 * réduits à la méthode, l'adresse nettoyée et le statut.
 */
export function nettoyerFilAriane(element) {
    // Le relais reçoit ce que le réseau lui donne : `null`, un nombre, un tableau.
    if (!estObjet(element))
        return null;
    const brut = element;
    const categorie = brut.category;
    if (typeof categorie !== 'string' || !CATEGORIES_FIL.includes(categorie)) {
        return null;
    }
    const propre = recopier(brut, ['timestamp', 'category', 'type', 'level']);
    if (estObjet(brut.data)) {
        const donnees = {};
        for (const champ of ['url', 'from', 'to']) {
            const valeur = brut.data[champ];
            if (typeof valeur === 'string')
                donnees[champ] = adresseSansDonnees(valeur);
        }
        for (const champ of ['method', 'status_code']) {
            const valeur = brut.data[champ];
            if (estScalaire(valeur))
                donnees[champ] = valeur;
        }
        propre.data = donnees;
    }
    return propre;
}
/**
 * L'événement réduit à ce qui peut quitter la plateforme. Une entrée qui
 * n'est pas un objet (charge `null` reçue par le relais) donne un objet vide.
 */
export function nettoyerEvenement(evenement) {
    if (!estObjet(evenement))
        return {};
    const brut = evenement;
    const propre = recopier(brut, CHAMPS_RACINE);
    if (typeof brut.transaction === 'string') {
        propre.transaction = adresseSansDonnees(brut.transaction);
    }
    const exceptions = estObjet(brut.exception) ? brut.exception.values : undefined;
    if (Array.isArray(exceptions)) {
        propre.exception = { values: exceptions.map(nettoyerException).filter((e) => e !== null) };
    }
    // Un `captureMessage` : son texte est traité comme le message d'une exception.
    if (brut.message !== undefined || brut.logentry !== undefined) {
        propre.message = VALEUR_EXPURGEE;
    }
    const sections = {
        request: nettoyerRequete(brut.request),
        user: nettoyerUtilisateur(brut.user),
        tags: nettoyerEtiquettes(brut.tags),
        contexts: nettoyerContextes(brut.contexts),
    };
    for (const [nom, valeur] of Object.entries(sections)) {
        if (valeur !== undefined)
            propre[nom] = valeur;
    }
    if (Array.isArray(brut.breadcrumbs)) {
        propre.breadcrumbs = brut.breadcrumbs
            .filter(estObjet)
            .map((element) => nettoyerFilAriane(element))
            .filter((element) => element !== null);
    }
    return propre;
}
//# sourceMappingURL=nettoyage.js.map